import Device from '@/utils/device';
import {
  apiTrackingActiveConfig,
  apiTrackingEventReport,
} from '@/api/tracking';
import { ITrackingPoint } from '@/api/typings/tracking';
import {
  TrackingClientType,
  TrackingEventType,
  TrackingPointStatus,
  TrackingTrackType,
  TRACKING_GLOBAL_PAGE_PATH,
} from '@/constants/business/tracking';
import {
  extractDataTrackIdFromXpath,
  isElementVisible,
  resolveTrackingElements,
} from './xpath';
import { logger } from './logger';

const EXPOSURE_VISIBLE_MS = 1000;
const REPORT_FLUSH_MS = 1000;
const REPORT_BATCH_SIZE = 20;
const FINGERPRINT_CANDIDATE_LIMIT = 500;
const FINGERPRINT_MATCH_SCORE = 65;
const FINGERPRINT_SCORE_MARGIN = 15;
const OVERLAY_FINGERPRINT_ROLES = [
  'option',
  'treeitem',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
];

export interface TrackingAncestorFingerprint {
  tagName: string;
  role?: string;
  classTokens?: string[];
}

export interface TrackingElementFingerprint {
  tagName: string;
  role?: string;
  text?: string;
  href?: string;
  stableAttributes?: Record<string, string>;
  classTokens?: string[];
  ancestorChain?: TrackingAncestorFingerprint[];
}

export interface TrackingLocatorTrigger {
  xpath?: string;
  fingerprint?: TrackingElementFingerprint;
}

export interface TrackingLocator {
  version?: 2;
  xpath: string;
  tagName?: string;
  text?: string;
  fingerprint?: TrackingElementFingerprint;
  inOverlay?: boolean;
  trigger?: TrackingLocatorTrigger;
  rect?: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
}

export interface TrackingRuntimeConfig {
  id: number;
  name: string;
  trackType: TrackingTrackType;
  pagePath?: string;
  selector: string;
  locator: TrackingLocator;
  clientType: TrackingClientType;
}

export type TrackingLocatorMatchType =
  | 'exact'
  | 'fingerprint'
  | 'ambiguous'
  | 'missing';

export interface TrackingLocatorMatch {
  elements: Element[];
  type: TrackingLocatorMatchType;
  score?: number;
}

export interface InitVisualTrackingReporterOptions {
  configs: TrackingRuntimeConfig[];
  orgId: string;
  accountId?: number | string;
  root?: Document;
  win?: Window;
  clientType?: TrackingClientType;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim()
    ? value.trim().slice(0, 200)
    : undefined;
}

function readStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const strings = value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim().slice(0, 80))
    .filter(Boolean)
    .slice(0, 10);
  return strings.length ? strings : undefined;
}

function parseFingerprint(
  value: unknown
): TrackingElementFingerprint | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const tagName = readOptionalString(value.tagName)?.toLowerCase();
  if (!tagName) {
    return undefined;
  }

  const stableAttributes: Record<string, string> = {};
  const rawStableAttributes = value.stableAttributes;
  if (isRecord(rawStableAttributes)) {
    Object.keys(rawStableAttributes)
      .slice(0, 10)
      .forEach((name) => {
        const attributeValue = readOptionalString(rawStableAttributes[name]);
        if (attributeValue) {
          stableAttributes[name] = attributeValue;
        }
      });
  }

  const ancestorChain: TrackingAncestorFingerprint[] = [];
  if (Array.isArray(value.ancestorChain)) {
    value.ancestorChain.slice(0, 5).forEach((ancestor) => {
      if (!isRecord(ancestor)) {
        return;
      }
      const ancestorTagName = readOptionalString(
        ancestor.tagName
      )?.toLowerCase();
      if (!ancestorTagName) {
        return;
      }
      ancestorChain.push({
        tagName: ancestorTagName,
        role: readOptionalString(ancestor.role)?.toLowerCase(),
        classTokens: readStringArray(ancestor.classTokens),
      });
    });
  }

  return {
    tagName,
    role: readOptionalString(value.role)?.toLowerCase(),
    text: readOptionalString(value.text),
    href: readOptionalString(value.href),
    stableAttributes: Object.keys(stableAttributes).length
      ? stableAttributes
      : undefined,
    classTokens: readStringArray(value.classTokens),
    ancestorChain: ancestorChain.length ? ancestorChain : undefined,
  };
}

function parseTrigger(value: unknown): TrackingLocatorTrigger | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const xpath = readOptionalString(value.xpath);
  const fingerprint = parseFingerprint(value.fingerprint);
  if (!xpath && !fingerprint) {
    return undefined;
  }
  return {
    xpath,
    fingerprint,
  };
}

/** 去掉 /org/:orgId 前缀，采集与预览共用同一套规则 */
export function stripOrgPrefix(pathname: string, orgId?: string): string {
  const normalizedPathname =
    typeof pathname === 'string' && pathname ? pathname : '/';
  const orgPrefix = orgId ? '/org/' + orgId : '';

  if (orgPrefix && normalizedPathname.indexOf(orgPrefix + '/') === 0) {
    return normalizedPathname.slice(orgPrefix.length) || '/';
  }

  if (orgPrefix && normalizedPathname === orgPrefix) {
    return '/';
  }

  return normalizedPathname;
}

export function parseLocatorJson(locatorJson?: string): TrackingLocator | null {
  if (!locatorJson || !locatorJson.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(locatorJson) as TrackingLocator;
    if (!parsed || typeof parsed.xpath !== 'string' || !parsed.xpath.trim()) {
      return null;
    }
    return {
      version: parsed.version === 2 ? 2 : undefined,
      xpath: parsed.xpath.trim(),
      tagName: parsed.tagName,
      text: parsed.text,
      fingerprint: parseFingerprint(parsed.fingerprint),
      inOverlay: parsed.inOverlay === true ? true : undefined,
      trigger: parseTrigger(parsed.trigger),
      rect: parsed.rect,
    };
  } catch (error) {
    logger.warn('parse tracking locatorJson failed', error);
    return null;
  }
}

export function serializeLocatorJson(locator: TrackingLocator): string {
  return JSON.stringify({
    version: locator.fingerprint ? 2 : undefined,
    xpath: locator.xpath,
    tagName: locator.tagName || '',
    text: locator.text || '',
    fingerprint: locator.fingerprint,
    inOverlay: locator.inOverlay || undefined,
    trigger: locator.trigger,
    rect: locator.rect,
  });
}

export function isOverlayTrackingLocator(locator: TrackingLocator): boolean {
  if (locator.inOverlay) {
    return true;
  }
  const role = locator.fingerprint?.role;
  return Boolean(role && OVERLAY_FINGERPRINT_ROLES.indexOf(role) !== -1);
}

export function hasTrackingLocatorTrigger(locator: TrackingLocator): boolean {
  return Boolean(locator.trigger?.xpath || locator.trigger?.fingerprint);
}

export function isTrackingLocatorTriggerVisible(
  locator: TrackingLocator,
  root: Document | Element
): boolean {
  if (!hasTrackingLocatorTrigger(locator)) {
    return false;
  }
  const trigger = locator.trigger;
  const match = resolveTrackingLocator(
    {
      xpath: trigger?.xpath || '',
      fingerprint: trigger?.fingerprint,
      tagName: trigger?.fingerprint?.tagName,
    },
    root
  );
  return match.elements.some((element) => isElementVisible(element));
}

function normalizeFingerprintText(value?: string): string {
  return (value || '').replace(/\s+/g, ' ').trim().slice(0, 80);
}

export function isStableFingerprintClassToken(token: string): boolean {
  if (!token || token.length > 80) {
    return false;
  }
  if (/^(css-|:r)/i.test(token)) {
    return false;
  }
  if (/^_[a-z0-9]{6,}$/i.test(token) || /^[a-f0-9]{8,}$/i.test(token)) {
    return false;
  }
  if (/-(open|hover)$/i.test(token)) {
    return false;
  }
  const hashed = token.match(/--([A-Za-z0-9]{4,8})$/);
  if (hashed && (/[A-Z]/.test(hashed[1]) || /\d/.test(hashed[1]))) {
    return false;
  }
  return true;
}

function getElementClassTokens(element: Element): string[] {
  const className =
    typeof element.className === 'string'
      ? element.className
      : typeof (element.className as { baseVal?: string })?.baseVal === 'string'
      ? (element.className as { baseVal: string }).baseVal
      : '';
  return className.split(/\s+/).filter(isStableFingerprintClassToken);
}

function getTokenSimilarity(source?: string[], target?: string[]): number {
  if (!source?.length || !target?.length) {
    return 0;
  }
  const targetSet = new Set(target);
  let common = 0;
  source.forEach((token) => {
    if (targetSet.has(token)) {
      common += 1;
    }
  });
  return common / Math.max(source.length, target.length);
}

function getTextSimilarity(source?: string, target?: string): number {
  const left = normalizeFingerprintText(source);
  const right = normalizeFingerprintText(target);
  if (!left || !right) {
    return 0;
  }
  if (left === right) {
    return 1;
  }
  if (left.indexOf(right) !== -1 || right.indexOf(left) !== -1) {
    return (
      Math.min(left.length, right.length) / Math.max(left.length, right.length)
    );
  }
  return 0;
}

function scoreAncestorChain(
  element: Element,
  ancestors?: TrackingAncestorFingerprint[]
): number {
  if (!ancestors?.length) {
    return 0;
  }
  let current = element.parentElement;
  let score = 0;
  for (
    let index = 0;
    index < ancestors.length && current;
    index += 1, current = current.parentElement
  ) {
    const ancestor = ancestors[index];
    if (current.tagName.toLowerCase() === ancestor.tagName) {
      score += 5;
    }
    const role = (current.getAttribute('role') || '').trim().toLowerCase();
    if (ancestor.role && role === ancestor.role) {
      score += 3;
    }
    score +=
      getTokenSimilarity(
        ancestor.classTokens?.filter(isStableFingerprintClassToken),
        getElementClassTokens(current)
      ) * 4;
  }
  return Math.min(score, 25);
}

export function scoreElementFingerprint(
  element: Element,
  fingerprint: TrackingElementFingerprint
): number {
  if (element.tagName.toLowerCase() !== fingerprint.tagName) {
    return 0;
  }

  let score = 15;
  const role = (element.getAttribute('role') || '').trim().toLowerCase();
  if (fingerprint.role) {
    score += role === fingerprint.role ? 15 : -10;
  }

  const stableAttributes = fingerprint.stableAttributes || {};
  const stableAttributeNames = Object.keys(stableAttributes);
  for (let index = 0; index < stableAttributeNames.length; index += 1) {
    const name = stableAttributeNames[index];
    const actual = element.getAttribute(name);
    if (actual && actual !== stableAttributes[name]) {
      return 0;
    }
    if (!actual) {
      score -= 10;
    } else if (name === 'data-track-id') {
      score += 100;
    } else if (name === 'data-testid' || name === 'id') {
      score += 90;
    } else {
      score += 40;
    }
  }

  if (fingerprint.href) {
    score += element.getAttribute('href') === fingerprint.href ? 40 : -10;
  }
  score +=
    getTextSimilarity(fingerprint.text, element.textContent || undefined) * 35;
  score +=
    getTokenSimilarity(
      fingerprint.classTokens?.filter(isStableFingerprintClassToken),
      getElementClassTokens(element)
    ) * 20;
  score += scoreAncestorChain(element, fingerprint.ancestorChain);
  return score;
}

function getFingerprintCandidates(
  fingerprint: TrackingElementFingerprint,
  root: Document | Element
): Element[] {
  const result: Element[] = [];
  const seen = new Set<Element>();
  const pushUnique = (element: Element) => {
    if (seen.has(element) || result.length >= FINGERPRINT_CANDIDATE_LIMIT) {
      return;
    }
    seen.add(element);
    result.push(element);
  };

  if (fingerprint.role) {
    const roleNodes = root.querySelectorAll(
      '[role="' + fingerprint.role + '"]'
    );
    for (let index = 0; index < roleNodes.length; index += 1) {
      pushUnique(roleNodes[index]);
    }
    if (result.length) {
      return result;
    }
  }

  const tagElements = root.getElementsByTagName(fingerprint.tagName);
  const text = normalizeFingerprintText(fingerprint.text);
  if (text) {
    for (let index = 0; index < tagElements.length; index += 1) {
      const element = tagElements[index];
      const elementText = normalizeFingerprintText(element.textContent || '');
      if (elementText === text || elementText.indexOf(text) !== -1) {
        pushUnique(element);
      }
    }
    if (result.length) {
      return result;
    }
  }

  for (
    let index = 0;
    index < tagElements.length && result.length < FINGERPRINT_CANDIDATE_LIMIT;
    index += 1
  ) {
    pushUnique(tagElements[index]);
  }
  return result;
}

function resolveLegacyLocator(
  locator: TrackingLocator,
  root: Document | Element
): TrackingLocatorMatch {
  const tagName = locator.tagName?.trim();
  const text = normalizeFingerprintText(locator.text);
  if (!tagName || !text) {
    return { elements: [], type: 'missing' };
  }

  const matched: Element[] = [];
  const elements = root.getElementsByTagName(tagName);
  for (let index = 0; index < elements.length; index += 1) {
    const elementText = normalizeFingerprintText(
      elements[index].textContent || ''
    );
    if (elementText === text || elementText.indexOf(text) === 0) {
      matched.push(elements[index]);
    }
  }
  return {
    elements: matched,
    type: matched.length ? 'exact' : 'missing',
  };
}

export function resolveTrackingLocator(
  locator: TrackingLocator,
  root: Document | Element
): TrackingLocatorMatch {
  const trackId =
    locator.fingerprint?.stableAttributes?.['data-track-id'] ||
    extractDataTrackIdFromXpath(locator.xpath);
  if (trackId) {
    let elements: Element[] = [];
    try {
      elements = Array.prototype.slice.call(
        root.querySelectorAll('[data-track-id=' + JSON.stringify(trackId) + ']')
      ) as Element[];
    } catch (error) {
      logger.warn('query tracking data-track-id failed', error);
    }
    if (elements.length === 1 || (!locator.fingerprint && elements.length)) {
      return { elements, type: 'exact' };
    }
    if (elements.length > 1) {
      return { elements: [], type: 'ambiguous' };
    }
  }

  const byXpath = resolveTrackingElements(locator.xpath, root);
  const visibleXpath = byXpath.filter(isElementVisible);
  if (!locator.fingerprint) {
    if (visibleXpath.length) {
      return { elements: visibleXpath, type: 'exact' };
    }
    if (byXpath.length) {
      return { elements: byXpath, type: 'exact' };
    }
    return resolveLegacyLocator(locator, root);
  }

  if (visibleXpath.length === 1) {
    return { elements: visibleXpath, type: 'exact' };
  }

  const candidates = visibleXpath.length
    ? visibleXpath
    : getFingerprintCandidates(locator.fingerprint, root);
  const scored = candidates
    .map((element) => ({
      element,
      score: scoreElementFingerprint(element, locator.fingerprint!),
    }))
    .sort((left, right) => right.score - left.score);
  const best = scored[0];
  const second = scored[1];
  if (!best || best.score < FINGERPRINT_MATCH_SCORE) {
    return { elements: [], type: 'missing' };
  }
  if (second && best.score - second.score < FINGERPRINT_SCORE_MARGIN) {
    return { elements: [], type: 'ambiguous', score: best.score };
  }
  return {
    elements: [best.element],
    type: visibleXpath.indexOf(best.element) !== -1 ? 'exact' : 'fingerprint',
    score: best.score,
  };
}

export function toRuntimeConfigs(
  points: ITrackingPoint[] | undefined,
  clientType: TrackingClientType
): TrackingRuntimeConfig[] {
  if (!Array.isArray(points)) {
    return [];
  }

  const configs: TrackingRuntimeConfig[] = [];
  points.forEach((point) => {
    const locator = parseLocatorJson(point.locatorJson);
    const id = point.id;
    const trackType = point.trackType;
    if (
      id == null ||
      !point.name ||
      !locator ||
      (point.pointStatus != null &&
        point.pointStatus !== TrackingPointStatus.Published) ||
      (trackType !== TrackingTrackType.Click &&
        trackType !== TrackingTrackType.Exposure &&
        trackType !== TrackingTrackType.Both)
    ) {
      return;
    }

    configs.push({
      id,
      name: point.name,
      trackType,
      pagePath: point.pagePath,
      selector: locator.xpath,
      locator,
      clientType: point.clientType || clientType,
    });
  });
  return configs;
}

export async function loadVisualTrackingRuntimeConfigs(
  clientType: TrackingClientType
): Promise<TrackingRuntimeConfig[]> {
  const response = await apiTrackingActiveConfig({ clientType });
  return toRuntimeConfigs(response.list, clientType);
}

function toTrackingPathname(path?: string): string {
  if (!path) {
    return '';
  }
  const trimmed = path.trim();
  if (!trimmed) {
    return '';
  }
  const pathname = trimmed.split('?')[0].split('#')[0];
  if (!pathname) {
    return '';
  }
  return pathname.charAt(0) === '/' ? pathname : '/' + pathname;
}

export function matchRoutePath(pattern: string, pathname: string): boolean {
  const patternParts = pattern.split('/');
  const pathParts = pathname.split('/');
  if (patternParts.length !== pathParts.length) {
    return false;
  }
  for (let i = 0; i < patternParts.length; i += 1) {
    const token = patternParts[i];
    if (token.charAt(0) === ':') {
      if (!pathParts[i]) {
        return false;
      }
      continue;
    }
    if (token !== pathParts[i]) {
      return false;
    }
  }
  return true;
}

export function isSameTrackingPage(
  configPagePath: string | undefined,
  currentPath: string
): boolean {
  const path = toTrackingPathname(configPagePath);
  const pagePath = toTrackingPathname(currentPath);
  if (!path || path === TRACKING_GLOBAL_PAGE_PATH) {
    return true;
  }
  if (path === pagePath) {
    return true;
  }
  if (path.indexOf(':') >= 0) {
    return matchRoutePath(path, pagePath);
  }
  return false;
}

function isMatchedElement(config: TrackingRuntimeConfig, target: Element) {
  const elements = resolveTrackingLocator(
    config.locator,
    target.ownerDocument
  ).elements;
  return elements.some((element) => {
    return element === target || element.contains(target);
  });
}

function createEventReporter(options: {
  clientType: TrackingClientType;
  win: Window;
}) {
  const queue: Array<{
    pointId: number;
    eventType: TrackingEventType;
    eventTime: number;
  }> = [];
  let timer: number | undefined;
  let flushing = false;

  const flush = () => {
    if (flushing || !queue.length) {
      return;
    }

    flushing = true;
    const events = queue.splice(0, REPORT_BATCH_SIZE);
    apiTrackingEventReport({
      clientType: options.clientType,
      events,
    })
      .catch((error) => {
        logger.warn('visual tracking report failed', error);
      })
      .finally(() => {
        flushing = false;
        if (queue.length) {
          schedule();
        }
      });
  };

  const schedule = () => {
    if (timer != null) {
      return;
    }
    timer = options.win.setTimeout(() => {
      timer = undefined;
      flush();
    }, REPORT_FLUSH_MS);
  };

  const enqueue = (pointId: number, eventType: TrackingEventType) => {
    queue.push({
      pointId,
      eventType,
      eventTime: Math.floor(Date.now() / 1000),
    });
    if (queue.length >= REPORT_BATCH_SIZE) {
      if (timer != null) {
        options.win.clearTimeout(timer);
        timer = undefined;
      }
      flush();
      return;
    }
    schedule();
  };

  const onHide = () => {
    if (timer != null) {
      options.win.clearTimeout(timer);
      timer = undefined;
    }
    flush();
  };

  const onVisibilityChange = () => {
    if (options.win.document.visibilityState === 'hidden') {
      onHide();
    }
  };

  options.win.addEventListener('pagehide', onHide);
  options.win.addEventListener('visibilitychange', onVisibilityChange);

  return {
    reportClick(pointId: number) {
      enqueue(pointId, TrackingEventType.Click);
    },
    reportExposure(pointId: number) {
      enqueue(pointId, TrackingEventType.Exposure);
    },
    destroy() {
      onHide();
      options.win.removeEventListener('pagehide', onHide);
      options.win.removeEventListener('visibilitychange', onVisibilityChange);
    },
  };
}

export function initVisualTrackingReporter(
  options: InitVisualTrackingReporterOptions
): () => void {
  const root =
    options.root || (typeof document === 'undefined' ? undefined : document);
  const win =
    options.win || (typeof window === 'undefined' ? undefined : window);

  if (!root || !win || !options.orgId) {
    return () => undefined;
  }

  const trackingRoot = root;
  const trackingWindow = win;
  const clientType =
    options.clientType ||
    (Device.isMobile() ? TrackingClientType.Mobile : TrackingClientType.PC);
  const configs = Array.isArray(options.configs) ? options.configs : [];

  if (!configs.length) {
    return () => undefined;
  }

  const reporter = createEventReporter({ clientType, win: trackingWindow });
  const clickConfigs = configs.filter((config) => {
    return (
      config.trackType === TrackingTrackType.Click ||
      config.trackType === TrackingTrackType.Both
    );
  });
  const exposureConfigs = configs.filter((config) => {
    return (
      config.trackType === TrackingTrackType.Exposure ||
      config.trackType === TrackingTrackType.Both
    );
  });

  function handleClick(event: MouseEvent) {
    try {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const pagePath = stripOrgPrefix(
        trackingWindow.location.pathname,
        options.orgId
      );
      const matchedConfig = clickConfigs.find((config) => {
        return (
          isSameTrackingPage(config.pagePath, pagePath) &&
          isMatchedElement(config, target)
        );
      });

      if (!matchedConfig) {
        return;
      }

      reporter.reportClick(matchedConfig.id);
    } catch (error) {
      logger.warn('visual tracking click handler failed', error);
    }
  }

  let intersectionObserver: IntersectionObserver | undefined;
  let mutationObserver: MutationObserver | undefined;
  let mutationTimer: number | undefined;
  const observedElements = new Set<Element>();
  const viewElementConfigMap = new Map<Element, TrackingRuntimeConfig[]>();
  const visibleTimers = new Map<string, number>();
  const firedWhileVisible = new Set<string>();
  const elementUids = new WeakMap<Element, number>();
  let elementUidSeed = 0;

  function getElementUid(element: Element): number {
    const existing = elementUids.get(element);
    if (existing != null) {
      return existing;
    }
    elementUidSeed += 1;
    elementUids.set(element, elementUidSeed);
    return elementUidSeed;
  }

  function getVisibilityKey(configId: number, element: Element) {
    return configId + '::' + getElementUid(element);
  }

  function clearVisibilityTimer(key: string) {
    const timerId = visibleTimers.get(key);
    if (timerId != null) {
      trackingWindow.clearTimeout(timerId);
      visibleTimers.delete(key);
    }
  }

  function pruneDisconnectedObservedElements() {
    observedElements.forEach((element) => {
      if (element.isConnected) {
        return;
      }
      const uid = String(getElementUid(element));
      observedElements.delete(element);
      viewElementConfigMap.delete(element);
      intersectionObserver?.unobserve(element);
      firedWhileVisible.forEach((key) => {
        if (key.slice(key.lastIndexOf('::') + 2) === uid) {
          firedWhileVisible.delete(key);
          clearVisibilityTimer(key);
        }
      });
    });
  }

  function observeExposureElements() {
    if (!intersectionObserver || !exposureConfigs.length) {
      return;
    }

    pruneDisconnectedObservedElements();

    const pagePath = stripOrgPrefix(
      trackingWindow.location.pathname,
      options.orgId
    );

    exposureConfigs.forEach((config) => {
      if (!isSameTrackingPage(config.pagePath, pagePath)) {
        return;
      }

      resolveTrackingLocator(config.locator, trackingRoot).elements.forEach(
        (element) => {
          if (!element.isConnected) {
            return;
          }
          const elementConfigs = viewElementConfigMap.get(element) || [];
          if (elementConfigs.indexOf(config) === -1) {
            elementConfigs.push(config);
            viewElementConfigMap.set(element, elementConfigs);
          }
          if (!observedElements.has(element)) {
            observedElements.add(element);
            intersectionObserver?.observe(element);
          }
        }
      );
    });
  }

  function handleExposureEntries(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      const target = entry.target;
      const elementConfigs = viewElementConfigMap.get(target) || [];
      const pagePath = stripOrgPrefix(
        trackingWindow.location.pathname,
        options.orgId
      );

      elementConfigs.forEach((config) => {
        if (!isSameTrackingPage(config.pagePath, pagePath)) {
          return;
        }

        const key = getVisibilityKey(config.id, target);

        if (!entry.isIntersecting) {
          clearVisibilityTimer(key);
          firedWhileVisible.delete(key);
          return;
        }

        if (firedWhileVisible.has(key) || visibleTimers.has(key)) {
          return;
        }

        const timerId = trackingWindow.setTimeout(() => {
          visibleTimers.delete(key);
          firedWhileVisible.add(key);
          reporter.reportExposure(config.id);
        }, EXPOSURE_VISIBLE_MS);

        visibleTimers.set(key, timerId);
      });
    });
  }

  if (clickConfigs.length) {
    trackingRoot.addEventListener('click', handleClick, true);
  }

  const observerWindow = trackingWindow as Window & {
    IntersectionObserver?: typeof IntersectionObserver;
    MutationObserver?: typeof MutationObserver;
  };

  if (
    exposureConfigs.length &&
    typeof observerWindow.IntersectionObserver === 'function'
  ) {
    intersectionObserver = new observerWindow.IntersectionObserver(
      handleExposureEntries
    );
    observeExposureElements();

    if (typeof observerWindow.MutationObserver === 'function') {
      mutationObserver = new observerWindow.MutationObserver(() => {
        if (mutationTimer != null) {
          trackingWindow.clearTimeout(mutationTimer);
        }
        mutationTimer = trackingWindow.setTimeout(() => {
          mutationTimer = undefined;
          observeExposureElements();
        }, 100);
      });
      mutationObserver.observe(
        trackingRoot.body || trackingRoot.documentElement,
        {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'],
        }
      );
    }
  }

  return () => {
    if (clickConfigs.length) {
      trackingRoot.removeEventListener('click', handleClick, true);
    }
    visibleTimers.forEach((timerId) => {
      trackingWindow.clearTimeout(timerId);
    });
    visibleTimers.clear();
    if (mutationTimer != null) {
      trackingWindow.clearTimeout(mutationTimer);
    }
    intersectionObserver?.disconnect();
    mutationObserver?.disconnect();
    reporter.destroy();
  };
}
